import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    Filter,
    EmailField,
    DateField,
    ShowButton,
    ChipField,
    FunctionField,
    SelectArrayInput,
    ReferenceInput,
    SelectInput,
    DateInput,
    ReferenceArrayInput,
    AutocompleteArrayInput,
    Link,
} from 'react-admin';
import moment from 'moment';
import CustomSearchInput from '../components/CustomSearchInput';
import BoxitPagination from '../components/BoxitPagination';
import BoxitChipField from '../components/BoxitChipField';
import MoneyField from '../components/MoneyField';
import CountryField from '../components/CountryField';
import UserReferenceField from '../components/UserReferenceField';
import { ListProps, User } from '../types';
import { UserStatus } from '../constants';
import { Chip } from '@material-ui/core';

const StaffUsersFilter = props => (
    <Filter variant="outlined" {...props}>
        <CustomSearchInput alwaysOn />
        <SelectArrayInput
            style={{ minWidth: '10rem' }}
            source="status"
            choices={Object.keys(UserStatus).map(c => ({ id: c, name: c }))}
            // alwaysOn
        />

        <ReferenceInput
            source="partnerId"
            reference="partners"
            resource="partners"
            label="Partner"
        >
            <SelectInput />
        </ReferenceInput>

        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput />
        </ReferenceInput>

        <ReferenceArrayInput
            source="tagsand"
            reference="tags"
            label="Tags (And)"
        >
            <AutocompleteArrayInput />
        </ReferenceArrayInput>
        <ReferenceArrayInput source="tagsor" reference="tags" label="Tags (Or)">
            <AutocompleteArrayInput />
        </ReferenceArrayInput>

        <DateInput
            source="createdAt[$gte]"
            label="Created From"
            parse={v => v && moment(v).startOf('day').format()}
        />
        <DateInput
            source="createdAt[$lte]"
            label="Created To"
            parse={v => v && moment(v).endOf('day').format()}
        />
    </Filter>
);

// Custom field components with links
const PartnerField = ({ record }) => {
    if (!record || !record.partner) return null;
    return (
        <Link to={`/partners/${record.partner.id}/show`}>
            {record.partner.name}
        </Link>
    );
};

const CountryNameField = ({ record }) => {
    if (!record || !record.country) return null;
    return (
        <Link to={`/country/${record.country.id}/show`}>
            {record.country.name}
        </Link>
    );
};

const AssigneeField = ({ record }) => {
    if (!record || !record.assignee) return null;
    return (
        <Link to={`/users/${record.assignee.id}/show`}>
            {record.assignee.name}
        </Link>
    );
};

const StaffUsersList: React.FC<ListProps<User>> = ({
    permissions = [],
    ...props
}) => {
    // Add basePath to filter to identify staff users list requests
    const filterDefaultValues = { basePath: '/staff-users' };

    return (
        <List
            title="Staff Users"
            bulkActionButtons={false}
            filters={<StaffUsersFilter />}
            sort={{ field: 'id', order: 'DESC' }}
            pagination={<BoxitPagination />}
            perPage={10}
            filterDefaultValues={filterDefaultValues}
            basePath="/users"
            {...props}
        >
            <Datagrid rowClick={(id, basePath, record) => `/users/${id}/show`}>
                <TextField source="serial" sortable={false} />
                <TextField source="id" />
                {/* <FunctionField
                    label="Partner"
                    render={record => <PartnerField record={record} />}
                /> */}
                <FunctionField
                    label="Type"
                    render={record => <Chip label={record.type} />}
                />
                <TextField source="name" />
                <EmailField source="email" />
                <TextField source="phone" />
                <FunctionField
                    label="Assignee"
                    render={record => <AssigneeField record={record} />}
                />
                <FunctionField
                    label="Country"
                    render={record => <CountryNameField record={record} />}
                />
                {permissions.indexOf('partner') === -1 && (
                    <MoneyField source="credits" showCurrency />
                )}
                <FunctionField
                    label="Tags"
                    render={record =>
                        record.tags
                            ? record.tags.length > 3
                                ? record.tags
                                      .map(item => item.name)
                                      .slice(0, 3)
                                      .join(', ') + '...'
                                : record.tags
                                      .map(item => item.name)
                                      .slice(0, 3)
                                      .join(', ')
                            : ''
                    }
                />
                <BoxitChipField record source="status" list="user" />
                <DateField source="createdAt" label="Created" showTime />
                {props.hasShow && (
                    <ShowButton style={{ padding: 0 }} basePath="/users" />
                )}
            </Datagrid>
        </List>
    );
};

StaffUsersList.defaultProps = {
    resource: 'users',
    location: { search: '' },
    hasShow: true,
};

export default StaffUsersList;
